<template>
  <form novalidate @submit="submit($event)">
    <JRow spaceMd="12" spaceLg="12" spaceXl="12" class="mb-16 gap-y-16">
      <JTabs
        :tabItems="tabs"
        as="div"
        onlyTabs
        class="block w-full bg-white focus:outline-none md:px-16"
        tabListClass="w-100 !space-x-0  border-1 my-16 flex rounded-md overflow-hidden border-line space-x-0 [&>button:first-child]:rounded-s-md [&>button:last-child]:rounded-e-md"
        tabClass="w-full py-8 overflow-hidden"
        buttonClass="border border-line"
        selectedClass="bg-line font-medium"
        modelOptions
        @update:model-value="setTab"
      />
      <JCol
        v-for="preference in travelPreferences[tabSection as string]"
        :key="preference.type"
        :md="preference.type === 'hotelRating' ? '12' : '6'"
        :lg="preference.type === 'hotelRating' ? '8' : '6'"
        :xl="preference.type === 'hotelRating' ? '8' : '6'"
      >
        <div v-if="preference.type === 'hotelRating'">
          <label class="input__label block">
            {{ $t(`profile.${preference.name}`) }}
          </label>
          <div class="space-y-4">
            <div
              v-for="({ title, value }, index) in hotelStarRatingOptions"
              :key="index"
              class="flex min-h-[2.2rem] items-center gap-4"
              @click="internalTravelPreferences[preference.type] = value"
            >
              <JRadio
                :title="title"
                class="cursor-pointer leading-[1.3rem] [&>span.border-primary]:top-[50%] [&>span.border-primary]:-translate-y-[50%]"
                :value="value"
                :checked="internalTravelPreferences[preference.type] === value"
              />
              <JRating class="gap-x-1" :modelValue="value" size="12" />
            </div>
          </div>
        </div>
        <JTextInput
          v-else-if="preference.inputType === 'number'"
          v-model="internalTravelPreferences[preference.type]"
          type="number"
          :label="$t(preference.name)"
          :placeholder="
            $t('profile.enterField', { field: $t(preference.name) })
          "
          :error="getFieldError(preference.type)"
        />
        <div v-else-if="preference.inputType === 'counter'">
          <div class="input__label mb-16">
            {{ $t(preference.name) }}
          </div>
          <JCounter
            v-model="internalTravelPreferences[preference.type]"
            :min="preference.min"
            :max="preference.max"
          />
        </div>
        <JDateTimePicker
          v-else-if="preference.inputType === 'time-picker'"
          v-model="internalTravelPreferences[preference.type]"
          timePicker
          :label="$t(preference.name)"
          :disabled="
            preference.type === 'dontDisturbEndTime'
              ? doNotDisturbEndTimeDisabled
              : false
          "
          :minTime="calculateMinTime(preference.type)"
          :startTime="calculateMinTime(preference.type)"
          :maxTime="calculateMaxTime(preference.type)"
          :placeholder="
            $t('profile.enterField', { field: $t(preference.name) })
          "
          :required="
            preference.type === 'dontDisturbEndTime' ||
            preference.type === 'dontDisturbStartTime'
              ? doNotDisturbRequired
              : false
          "
        />
        <JSelect
          v-else
          v-model="internalTravelPreferences[preference.type]"
          :options="getLocalizedOptions(preference.data!)"
          optionKey="value"
          optionValue="title"
          :label="$t(`profile.${preference.name}`)"
          :placeholder="
            $t('profile.enterField', {
              field: $t(`profile.${preference.name}`),
            })
          "
        />
      </JCol>

      <template v-if="tabSection === 'Flight preferences'">
        <JCol md="6" lg="6" xl="6">
          <MultiDestinationSelectInput
            v-model="internalTravelPreferences.preferredConnectionPoints"
            :label="$t('profile.preferredConnectionPoints.label')"
            :placeholder="$t('profile.preferredConnectionPoints.placeholder')"
            onlyCities
            @update:model-value="onPreferredFieldUpdate"
          />
        </JCol>
        <JCol md="6" lg="6" xl="6">
          <MultiDestinationSelectInput
            v-model="internalTravelPreferences.nonPreferredConnectionPoints"
            :label="$t('profile.nonPreferredConnectionPoints.label')"
            :placeholder="
              $t('profile.nonPreferredConnectionPoints.placeholder')
            "
            :error="nonPreferredConnectionError"
            onlyCities
            @update:model-value="onPreferredFieldUpdate"
          />
        </JCol>
      </template>
    </JRow>

    <div class="flex md:justify-end">
      <JButton
        :disabled="!isChanged || !donNotDisturbIsValid || hasValidationErrors"
        size="md"
        :isLoading="isLoading"
        color="primary"
        :block="isMobile"
        type="submit"
      >
        {{ t('words.save') }}
      </JButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual'
import { computed, inject, ref, unref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { JTabItem } from '@journey/ui-kit'
import {
  JButton,
  JCol,
  JCounter,
  JDateTimePicker,
  JRadio,
  JRating,
  JRow,
  JSelect,
  JTabs,
  JTextInput,
} from '@journey/ui-kit'
import { type ValidationError, array, object, string } from 'yup'
import type { Props } from './MyTravelPreferencesFormFields.types'
import useProfileStore from '@/modules/Profile/store'
import useDeviceType from '@/modules/Core/composables/useDevicePlatform'
import {
  MY_TRAVEL_PREFERENCES_FORM_INJECTION_KEY,
  updateProfileSectionSharedStateInjectionKey,
} from '@/modules/Profile/constants'
import { useConvertTimeToObject } from '@/modules/Core/composables'
import MultiDestinationSelectInput from '@/modules/Search/components/MultiDestinationSelectInput/MultiDestinationSelectInput.vue'
import { shiftTimeByMinutes } from '@/modules/Core/utils'
import type { DestinationLocation } from '@/modules/Search/types/model/destination'
import type { UserTravelPreferences } from '@/modules/Profile/types/model/travelPreferences'
import featureFlag from '@/config/featureFlag'
import useCurrencyStore from '@/modules/Core/store/currency'
import { HOTEL_STAR_RATING_OPTIONS } from '@/modules/Search/components/SearchPanel/SearchPanelForm/SearchPanelFormOptions/SearchPanelFormOptionsHotel/SearchPanelFormOptionsHotel.constants'

const props = defineProps<Props>()

// I18N
const { t, te } = useI18n()

const nonPreferredConnectionError = ref<string>()

const fieldErrors = ref<Record<string, string>>({})

function getFieldError(fieldName: string): string | undefined {
  return fieldErrors.value[fieldName]
}

const connectionPointsValidationSchema = object({
  preferredConnectionPoints: array()
    .of(
      object({
        geoCountry: string(),
        geoCity: string(),
        geoContinent: string(),
        geoLocation: string(),
        locationType: string(),
        geoAreaState: string().nullable(),
      })
    )
    .nullable(),
  nonPreferredConnectionPoints: array()
    .of(
      object({
        geoCountry: string(),
        geoCity: string(),
        geoContinent: string(),
        geoLocation: string(),
        locationType: string(),
        geoAreaState: string().nullable(),
      })
    )
    .nullable()
    .test(
      'uniqueNonPreferred',
      t('profile.nonPreferredConnectionPoints.validation.uniqueNonPreferred'),
      function (value) {
        if (!value) {
          return true
        }

        // eslint-disable-next-line @typescript-eslint/no-invalid-this
        const { preferredConnectionPoints } = this.parent

        if (!preferredConnectionPoints) {
          return true
        }

        return value.every(
          (point) =>
            !preferredConnectionPoints.some(
              (preferredPoint: DestinationLocation) =>
                preferredPoint.geoLocation === point.geoLocation
            )
        )
      }
    ),
})

const currencyStore = useCurrencyStore()
async function validateField(fieldName: string, value: any) {
  try {
    fieldErrors.value[fieldName] = ''

    if (fieldName === 'minimumSwapAmount') {
      const minimumSwapAmountSchema = string().test(
        'min-value',
        t('profile.minimumSwapAmountValidation.min', {
          count: `${featureFlag.minimumSwapAmount} ${currencyStore.currency.title}`,
        }),
        function (value) {
          if (!value) {
            return true
          }

          const numValue = Number.parseFloat(value)
          return (
            !Number.isNaN(numValue) && numValue >= featureFlag.minimumSwapAmount
          )
        }
      )

      await minimumSwapAmountSchema.validate(value)
    }
  } catch (error: any) {
    fieldErrors.value[fieldName] = error.message
  }
}

function getLocalizedOptions(options: string[]) {
  return options.map((option) => ({
    title: te(`profile.${option}`) ? t(`profile.${option}`) : option,
    value: option,
  }))
}

// TABS
const tabSection = ref(Object.keys(props.travelPreferences)[0])

function setTab(newTabSection: JTabItem | number) {
  if (typeof newTabSection === 'number') {
    return
  }

  tabSection.value = newTabSection.value.toString()
}

const tabs = computed(() =>
  Object.keys(props.travelPreferences).map((groupName: string) => ({
    label: te(`profile.${groupName}`) ? t(`profile.${groupName}`) : groupName,
    value: groupName,
  }))
)

const internalTravelPreferences = ref<UserTravelPreferences>({
  ...props.userTravelPreferences,
})

const { isMobile } = useDeviceType()

watch(
  () => internalTravelPreferences.value.minimumSwapAmount,
  (newValue) => {
    if (newValue !== undefined) {
      validateField('minimumSwapAmount', newValue)
    }
  }
)

// SUBMISSION
const updateProfileSectionSharedState = inject(
  updateProfileSectionSharedStateInjectionKey
)

// DISABLED
const isChanged = computed(
  () => !isEqual(props.userTravelPreferences, internalTravelPreferences.value)
)

const hasValidationErrors = computed(() => {
  return Object.values(fieldErrors.value).some((error) => error !== '')
})

const doNotDisturbEndTimeDisabled = computed(
  () => !internalTravelPreferences.value.dontDisturbStartTime
)

const doNotDisturbRequired = computed(
  () =>
    !!internalTravelPreferences.value.dontDisturbStartTime ||
    !!internalTravelPreferences.value.dontDisturbEndTime
)
const donNotDisturbIsValid = computed(() =>
  doNotDisturbRequired.value
    ? !!internalTravelPreferences.value.dontDisturbStartTime &&
      !!internalTravelPreferences.value.dontDisturbEndTime
    : true
)
function calculateMinTime(preferenceType: string) {
  if (preferenceType !== 'dontDisturbEndTime') {
    return null
  }

  if (!internalTravelPreferences.value.dontDisturbStartTime) {
    return null
  }

  const { hours, minutes } = useConvertTimeToObject(
    shiftTimeByMinutes(internalTravelPreferences.value.dontDisturbStartTime, 1)
  )

  return {
    hours: Number.parseInt(hours.value),
    minutes: Number.parseInt(minutes.value),
  }
}

function calculateMaxTime(preferenceType: string) {
  if (preferenceType !== 'dontDisturbStartTime') {
    return null
  }

  if (!internalTravelPreferences.value.dontDisturbEndTime) {
    return null
  }

  const { hours, minutes } = useConvertTimeToObject(
    shiftTimeByMinutes(internalTravelPreferences.value.dontDisturbEndTime, -1)
  )

  return {
    hours: Number.parseInt(hours.value),
    minutes: Number.parseInt(minutes.value),
  }
}

watch(isChanged, (value) => {
  if (!updateProfileSectionSharedState) {
    return
  }

  updateProfileSectionSharedState.setTouched(
    MY_TRAVEL_PREFERENCES_FORM_INJECTION_KEY,
    value
  )
})

async function onPreferredFieldUpdate() {
  nonPreferredConnectionError.value = undefined

  try {
    const { nonPreferredConnectionPoints, preferredConnectionPoints } =
      internalTravelPreferences.value

    await connectionPointsValidationSchema.validate(
      { nonPreferredConnectionPoints, preferredConnectionPoints },
      {
        abortEarly: false,
      }
    )
  } catch (err) {
    const error = err as ValidationError
    nonPreferredConnectionError.value = error.errors[0]
  }
}

// SUBMIT
function submit($event: Event) {
  $event.preventDefault()

  if (nonPreferredConnectionError.value) {
    return
  }

  const hasFieldErrors = Object.values(fieldErrors.value).some(
    (error) => error !== ''
  )
  if (hasFieldErrors) {
    return
  }

  if (!isChanged.value) {
    return
  }

  if (!updateProfileSectionSharedState) {
    return
  }

  updateProfileSectionSharedState.items[
    MY_TRAVEL_PREFERENCES_FORM_INJECTION_KEY
  ].submit(internalTravelPreferences.value)

  updateProfileSectionSharedState.setTouched(
    MY_TRAVEL_PREFERENCES_FORM_INJECTION_KEY,
    false
  )
}

const profileStore = useProfileStore()

watch(
  internalTravelPreferences,
  (value) => {
    profileStore.setConfirmationPayload(isChanged.value ? value : null)
  },
  {
    deep: true,
  }
)

// LOADING
const isLoading = computed(() => {
  if (!updateProfileSectionSharedState) {
    return
  }

  return unref(
    updateProfileSectionSharedState?.items[
      MY_TRAVEL_PREFERENCES_FORM_INJECTION_KEY
    ].isLoading
  )
})

const hotelStarRatingOptions = computed(() => {
  return HOTEL_STAR_RATING_OPTIONS.map((item) => ({
    ...item,
    title: t(item.title),
  }))
})
</script>
